import { IVideoInfo } from 'mggp-player';

export enum ChannelType {
  REGULAR_CHANNEL = 'regularChannel',
  DVR_CHANNEL = 'dvrChannel',
  VOD_CHANNEL = 'vodChannel',
}

export type InteractiveChannelType =
  | ChannelType.DVR_CHANNEL
  | ChannelType.VOD_CHANNEL;

export interface IChannel {
  id: number;
  title: string;
  type: ChannelType;
  isLocked: boolean;
  currentProgram: IProgram | null;
  image: string;
  originalGenre: { id: number };
  isProgramAllocated: boolean;
  isInteractive: boolean;
  getNextProgram: (current: IProgram) => IProgram | null;
  getPrevProgram: (current: IProgram) => IProgram | null;
  _programs: IProgram[];
  get programs(): IProgram[];
  _programsMap: Map<number, IProgram>;
  _addProgram: (program: IProgram) => void;
}

export enum ProgramState {
  PAST = 0,
  CURRENT = 1,
  FUTURE = 2,
}

export interface IProgram {
  id: number;
  title: string;
  startTimestamp: number;
  endTimestamp: number;
  state: ProgramState;
  duration: number;
  channel: IChannel;
  virtualStream: IVideoInfo | null;
  virtualObjectId?: string;
  objectId?: number;
  groupedPrograms?: IAPIProgram['grouped_programs'];
}

export const PLAYER_PROPERTY = {
  currentProgramTime: 'epg:currentProgramTime',
  currentChannel: 'epg:currentChannel',
  currentProgram: 'epg:currentProgram',
  currentTimestamp: 'epg:currentTimestamp',
  playingProgram: 'interactiveChannel:currentPlayingProgram',
  channelState: 'interactiveChannel:channelState',
  interactiveChannelStartTime: 'epg2:interactiveChannelStartTime',
};

export const PLAYER_EVENT = {
  currentProgramChanged: 'epg:currentProgramChanged',
  channelSwitch: 'epg:channelSwitch',
  playProgram: (type: InteractiveChannelType) => `${type}:playProgram`,
  playingProgram: `property:${PLAYER_PROPERTY.playingProgram}`,
  backOnAir: (type: InteractiveChannelType) => `${type}:backOnAir`,
  interactiveChannelStartTime: `property:${PLAYER_PROPERTY.interactiveChannelStartTime}`,
  currentProgramListUpdated: 'epg:currentProgramListUpdated',
};

export const EPG2_PROPERTY = {
  // epg -> player
  epgTime: 'epg2:epgTime',
  api: 'epg2:api',
  setPlaying: 'epg2:setPlaying',
  onAirProgram: 'epg2:onAirProgram',

  // player -> epg
  playingProgram: 'epg2:playingProgram',
};

export const EPG2_EVENT = {
  // epg -> player
  epgTime: `property:${EPG2_PROPERTY.epgTime}`,
  api: `property:${EPG2_PROPERTY.api}`,
  setPlaying: `property:${EPG2_PROPERTY.setPlaying}`,
  onAirProgram: `property:${EPG2_PROPERTY.onAirProgram}`,
  channelClick: 'epg2:channelClick',
};

export interface IAPIChannel {
  id: number;
  title: string;
  is_available: boolean;
  free: boolean;
  vod_channel: boolean;
  epg_id: number;
  is_dvr: boolean;
  is_favorite: boolean;
  m_playlist: boolean;
  genres: number[];
  image: {
    small: string;
  };
}

export interface IAPIProgram {
  external_id: number;
  title: string;
  start_timestamp: number;
  end_timestamp: number;
  id: number;
  start: string;
  end: string;
  virtual_object_id?: string;
  object_id?: number;
  grouped_programs?: {
    before: Array<Record<string, string | number>>;
    after: Array<Record<string, string | number>>;
  };
}

export interface ISetPlayingData {
  channel: IAPIChannel;
  program: IAPIProgram;
  onAir: boolean;
  channelGenreId: number;
}

export type IOnAirProgramData = IAPIProgram;

export interface IEpgTimeData {
  timestamp: number;
}

export interface IEpgApi {
  fetchProgram: (
    channelId: number,
    timestamp: number
  ) => Promise<IAPIProgram | null>;
}
